import { useState } from 'react';
import type { UpdaterState } from '@teachpartner/contracts';
import { useServices } from '../app-context';
import { ErrorBanner } from '../components/badges';
import { RunHistory } from './updates/RunHistory';
import { RunPanel } from './updates/RunPanel';
import { UpdateActions } from './updates/UpdateActions';
import { UpdateSummary } from './updates/UpdateSummary';
import { useRunDetails, useUpdates } from './updates/use-updates';

/** Re-fetch a running run on every poll, a finished one once per status change. */
function runRefreshKey(state: UpdaterState | null, runId: string | null, revision: number) {
  const summary = state?.runs.find((r) => r.id === runId);
  if (!summary) return `missing-${revision}`;
  return summary.status === 'running' ? `running-${revision}` : summary.status;
}

function UnreachableBanner({ error }: { error: string | null }) {
  return (
    <div role="alert" className="banner banner-warn">
      The updater can&apos;t be reached{error ? `: ${error}.` : '.'} Check now, deploy and roll back
      will fail until it is back.
    </div>
  );
}

/** Central updater (LIVE L3): what central runs, the live or last run, history and actions. */
export function UpdatesPage() {
  const { api } = useServices();
  const updates = useUpdates(api);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const state = updates.view?.state ?? null;
  const runId = selectedId ?? state?.runs[0]?.id ?? null;
  const details = useRunDetails(api, runId, runRefreshKey(state, runId, updates.revision));

  const started = (id: string) => {
    setSelectedId(id);
    void updates.refresh();
  };

  const running = state?.runs.find((r) => r.id === runId)?.status === 'running';
  const title = running ? 'Running now' : selectedId ? 'Selected run' : 'Last run';

  return (
    <div>
      <h1>Updates</h1>
      <ErrorBanner message={updates.error} />
      {updates.view && !updates.view.reachable && <UnreachableBanner error={updates.view.error} />}
      {!updates.view && !updates.error && <p>Loading…</p>}
      {state && <UpdateSummary state={state} />}
      {state && <UpdateActions state={state} onStarted={started} />}
      {runId && <RunPanel run={details.run} error={details.error} title={title} />}
      {state && <RunHistory runs={state.runs} selectedId={runId} onSelect={setSelectedId} />}
    </div>
  );
}
